import { useState, VFC } from "react";

import {
  blue,
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControlLabel,
  grey,
  makeStyles,
  SvgDirectSailing,
  SvgDirectSailingChecked,
  SvgServiceDeferred,
  SvgServiceDeferredChecked,
  SvgServiceExpress,
  SvgServiceExpressChecked,
  SvgServiceStandard,
  SvgServiceStandardChecked,
  TextInput,
  Typography,
} from "@portex-pro/ui-components";
import { ServiceLevel } from "api/rest/quote-requests";
import cx from "clsx";
import xor from "lodash/xor";
import { Controller, FormProvider, UseFormReturn, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { StepBlock } from "../../_components/StepBlock";
import { useQuoteRequestSelector } from "../../_store";
import { quoteRequestActions } from "../../_store/quoteRequestSliceV2";
import { QuoteStepIndex } from "../../types";
import { FinalSchema } from "../_schemas";

interface ServiceStepTagProps {
  serviceLevels: ServiceLevel[];
}

const ServiceStepTag: React.FC<ServiceStepTagProps> = ({ serviceLevels }) => {
  const { t } = useTranslation(["shipper"]);

  if (!serviceLevels || serviceLevels.length === 0) {
    return null;
  }

  const services = [];

  if (serviceLevels.includes(ServiceLevel.Express)) {
    services.push(t("shipper:express"));
  }
  if (serviceLevels.includes(ServiceLevel.NonStop)) {
    services.push("Non-Stop");
  }
  if (serviceLevels.includes(ServiceLevel.Standard)) {
    services.push(t("shipper:standard"));
  }
  if (serviceLevels.includes(ServiceLevel.Deferred)) {
    services.push(t("shipper:deferred"));
  }

  const displayServices = services.slice(0, 2);
  const excessCount = services.length - 2;

  return (
    <div className="flex items-center space-x-1 text-blue-600 font-bold text-sm">
      <span>{displayServices.join(", ")}</span>
      {excessCount > 0 && <span>+{excessCount}</span>}
    </div>
  );
};

const useStyles = makeStyles(() => ({
  container: {
    borderStyle: "solid",
    borderWidth: "2px",
    borderColor: grey[200],
    borderRadius: "4px",
    width: "80%",
    marginBottom: "1rem",
  },
  row: {
    "&:hover": {
      background: blue[100],
      cursor: "pointer",
    },
  },
  activeRow: {
    background: blue[100],
  },
  label: {
    width: "100%",
    margin: 0,
    padding: "0.6rem 1rem",
    paddingLeft: "0.4rem",
    "& .MuiFormControlLabel-label": {
      width: "100%",
    },
  },
  labelRow: {
    "& svg": {
      minWidth: "53px",
    },
  },
}));

const ServiceStep: VFC<{ methods: UseFormReturn<FinalSchema>; onSubmit: (data: FinalSchema) => void }> = ({
  methods,
  onSubmit,
}) => {
  const { t } = useTranslation(["quoteRequest", "common", "shipper"]);
  const setQuoteRequestAction = quoteRequestActions.useSetQuoteRequest();
  const setStepIndex = quoteRequestActions.useSetStepIndex();
  const { stepIndex } = useQuoteRequestSelector((store) => store.quoteRequestSliceV2);
  const classes = useStyles();
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Watch serviceLevels for the tag
  const serviceLevels = useWatch({
    control: methods.control,
    name: "serviceLevels",
  });

  const submit = (data: FinalSchema) => {
    onSubmit(data);
    setStepIndex(QuoteStepIndex.CargoDetails);
    setIsCollapsed(true);
  };

  if (stepIndex < QuoteStepIndex.Service) {
    return null;
  }

  return (
    <FormProvider {...methods}>
      <div className="flex flex-col gap-6 w-full">
        <StepBlock
          title={t("common:serviceLevel")}
          headerBoxProps={{ onClick: () => setIsCollapsed(!isCollapsed) }}
          stepNumber={2}
          tag={<ServiceStepTag serviceLevels={serviceLevels || []} />}
          footer={
            stepIndex <= QuoteStepIndex.Service ? (
              <div className="flex justify-between mt-2 w-full">
                <div />
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={methods.handleSubmit(submit)}
                  style={{ width: 150 }}
                >
                  {t("common:next")}
                </Button>
              </div>
            ) : null
          }
        >
          <Collapse in={!isCollapsed}>
            <div className="flex flex-col mt-6">
              {methods.formState.errors.serviceLevels && (
                <Typography color="error" variant="caption">
                  {methods.formState.errors.serviceLevels.message}
                </Typography>
              )}
              <div className="border border-grey-200 rounded">
                <Controller
                  control={methods.control}
                  name="serviceLevels"
                  render={({ field }) => (
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      className={cx(classes.row, {
                        [classes.activeRow]: !!field.value?.includes(ServiceLevel.Express),
                      })}
                    >
                      <FormControlLabel
                        className={classes.label}
                        control={
                          <Checkbox
                            checked={!!field.value?.includes(ServiceLevel.Express)}
                            onChange={() =>
                              setQuoteRequestAction({
                                load_spec: { service_levels: xor(field.value, [ServiceLevel.Express]) },
                              })
                            }
                          />
                        }
                        label={
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            className={classes.labelRow}
                          >
                            <Box>
                              <Typography variant="subtitle1">{t("shipper:express")}</Typography>
                              <Typography variant="body2">
                                {t("shipper:serviceLevelStep.requestForFastService")}
                              </Typography>
                            </Box>
                            {!!field.value?.includes(ServiceLevel.Express) ? (
                              <SvgServiceExpressChecked />
                            ) : (
                              <SvgServiceExpress />
                            )}
                          </Box>
                        }
                      />
                    </Box>
                  )}
                />

                <Controller
                  control={methods.control}
                  name="serviceLevels"
                  render={({ field }) => (
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      className={cx(classes.row, {
                        [classes.activeRow]: !!field.value?.includes(ServiceLevel.NonStop),
                      })}
                    >
                      <FormControlLabel
                        className={classes.label}
                        control={
                          <Checkbox
                            checked={!!field.value?.includes(ServiceLevel.NonStop)}
                            onChange={() =>
                              setQuoteRequestAction({
                                load_spec: { service_levels: xor(field.value, [ServiceLevel.NonStop]) },
                              })
                            }
                          />
                        }
                        label={
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            className={classes.labelRow}
                          >
                            <Box>
                              <Typography variant="subtitle1">
                                {t("shipper:serviceLevelStep.nonStopService")}
                              </Typography>
                              <Typography variant="body2">{t("shipper:serviceLevelStep.requestForNonStop")}</Typography>
                            </Box>
                            {!!field.value?.includes(ServiceLevel.NonStop) ? (
                              <SvgDirectSailingChecked />
                            ) : (
                              <SvgDirectSailing />
                            )}
                          </Box>
                        }
                      />
                    </Box>
                  )}
                />

                <Controller
                  control={methods.control}
                  name="serviceLevels"
                  render={({ field }) => (
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      className={cx(classes.row, {
                        [classes.activeRow]: !!field.value?.includes(ServiceLevel.Standard),
                      })}
                    >
                      <FormControlLabel
                        className={classes.label}
                        control={
                          <Checkbox
                            checked={!!field.value?.includes(ServiceLevel.Standard)}
                            onChange={() =>
                              setQuoteRequestAction({
                                load_spec: { service_levels: xor(field.value, [ServiceLevel.Standard]) },
                              })
                            }
                          />
                        }
                        label={
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            className={classes.labelRow}
                          >
                            <Box>
                              <Typography variant="subtitle1">{t("shipper:standard")}</Typography>
                              <Typography variant="body2">{t("shipper:serviceLevelStep.requestForNormal")}</Typography>
                            </Box>
                            {!!field.value?.includes(ServiceLevel.Standard) ? (
                              <SvgServiceStandardChecked />
                            ) : (
                              <SvgServiceStandard />
                            )}
                          </Box>
                        }
                      />
                    </Box>
                  )}
                />

                <Controller
                  control={methods.control}
                  name="serviceLevels"
                  render={({ field }) => (
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      className={cx(classes.row, {
                        [classes.activeRow]: !!field.value?.includes(ServiceLevel.Deferred),
                      })}
                    >
                      <FormControlLabel
                        className={classes.label}
                        control={
                          <Checkbox
                            checked={!!field.value?.includes(ServiceLevel.Deferred)}
                            onChange={() =>
                              setQuoteRequestAction({
                                load_spec: { service_levels: xor(field.value, [ServiceLevel.Deferred]) },
                              })
                            }
                          />
                        }
                        label={
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            className={classes.labelRow}
                          >
                            <Box>
                              <Typography variant="subtitle1">{t("shipper:deferred")}</Typography>
                              <Typography variant="body2">
                                {t("shipper:serviceLevelStep.requestForNonUrgent")}
                              </Typography>
                            </Box>
                            {!!field.value?.includes(ServiceLevel.Deferred) ? (
                              <SvgServiceDeferredChecked />
                            ) : (
                              <SvgServiceDeferred />
                            )}
                          </Box>
                        }
                      />
                    </Box>
                  )}
                />
              </div>

              <div className="w-full mt-6">
                <Controller
                  control={methods.control}
                  name="carrierRoutingPref"
                  render={({ field }) => (
                    <TextInput
                      fullWidth
                      label={t("shipper:carrierAndRoutingPrefView.label")}
                      placeholder={t("shipper:carrierAndRoutingPrefView.placeholder")}
                      multiline
                      rows={3}
                      value={field.value}
                      onChange={(e) => setQuoteRequestAction({ carrier_routing_pref_notes: e.target.value })}
                    />
                  )}
                />
              </div>
            </div>
          </Collapse>
        </StepBlock>
      </div>
    </FormProvider>
  );
};

export default ServiceStep;
